import { Nullable } from 'global';
import { BasePrice } from 'modules/equipment/types';
import {
  ChargePrice,
  DiscountType,
  QuoteFeeResponse,
  QuoteCalcRowInput,
  QuoteCalcAttachType,
  QuoteCalcLineItemType,
  QuoteEquipmentResponse,
} from '@trnsact/trnsact-shared-types';

import { getRowKey } from '../lib/rowKey';
import { QuoteCalcRowPrice, QuoteCalcRowResult, QuoteCalcViewModel } from '../api/types';
import { QuoteChargeListingEntity, QuoteLineItemType } from '../types';

export interface SavedQuoteRowsSource {
  equipment?: Nullable<QuoteEquipmentResponse[]>;
  fees?: Nullable<QuoteFeeResponse[]>;
}

class QuoteCalcMapper {
  // Matches by row key so a stale calc never lands on a different row after inserts/removals
  public findCalcRow(
    calc: Nullable<QuoteCalcViewModel>,
    row: Nullable<QuoteChargeListingEntity>
  ): QuoteCalcRowResult | undefined {
    if (!calc?.rows?.length || !row) return undefined;

    const key = getRowKey(row);

    return key ? calc.rows.find(calcRow => calcRow.key === key) : undefined;
  }

  public toPreviewRows(rows: QuoteChargeListingEntity[] = []): QuoteCalcRowInput[] {
    return rows.map(row => {
      if (row.type === QuoteLineItemType.Equipment && row.equipment) {
        return {
          type: QuoteLineItemType.Equipment,
          equipment: {
            equipmentId: row.equipment.equipmentId,
            price: this.toCalcPrice(row.equipment.price),
            attachments: (row.equipment.attachments ?? []).map(attachment => ({
              price: this.toCalcPrice(attachment.price),
            })),
          },
        };
      }

      const charge = row.charge;

      return {
        type: QuoteLineItemType.Invoice,
        charge: charge
          ? {
              type: charge.type,
              id: charge.id ?? null,
              label: charge.label ?? null,
              amount: charge.amount ?? null,
              parentId: charge.parentId ?? null,
              isTaxable: charge.isTaxable ?? null,
              amountType: charge.amountType ?? null,
              parentType: charge.parentType ?? null,
              isFromCommonRules: charge.isFromCommonRules ?? false,
              customerVisibility: charge.customerVisibility ?? null,
              price: this.toCalcPrice(charge.price),
              cap: charge.cap
                ? {
                    min: charge.cap.min ?? null,
                    max: charge.cap.max ?? null,
                  }
                : null,
              groupBucket: {
                id: charge.groupBucket.id,
                type: charge.groupBucket.type,
              },
            }
          : null,
      };
    });
  }

  public toInputSignature(rows: QuoteChargeListingEntity[] = []): string {
    const computedOutputs = new Set(['total', 'netPrice', 'retailPrice']);

    return JSON.stringify(this.toPreviewRows(rows), (key, value) => (computedOutputs.has(key) ? undefined : value));
  }

  public fromSavedQuoteRows(voId: string, quote: Nullable<SavedQuoteRowsSource>): QuoteCalcRowInput[] {
    const equipmentList = (quote?.equipment ?? []).filter((item): item is QuoteEquipmentResponse => Boolean(item));
    const feeList = (quote?.fees ?? []).filter((item): item is QuoteFeeResponse => Boolean(item));
    const equipmentIds = new Set(equipmentList.map(equipment => equipment.equipmentId));

    const equipmentRows: QuoteCalcRowInput[] = equipmentList.map(equipment => ({
      type: QuoteCalcLineItemType.Equipment,
      equipment: {
        equipmentId: equipment.equipmentId,
        price: this.toChargePriceInput(equipment.price),
        attachments: [],
      },
    }));

    const feeRows: QuoteCalcRowInput[] = feeList.map(fee => {
      const parentId = fee.parentId ?? null;
      const parentType = parentId ? (equipmentIds.has(parentId) ? 'equipment' : 'charge') : null;

      return {
        type: QuoteCalcLineItemType.Invoice,
        charge: {
          id: fee.id ?? null,
          parentId,
          parentType,
          type: fee.type,
          label: fee.description,
          amount: fee.amount ?? null,
          isTaxable: fee.isTaxable ?? null,
          amountType: fee.amountType ?? null,
          isFromCommonRules: fee.isFromCommonRules ?? false,
          customerVisibility: fee.customerVisibility ?? null,
          price: this.toChargePriceInput(fee.price),
          cap: fee.cap ? { min: fee.cap.min ?? null, max: fee.cap.max ?? null } : null,
          groupBucket: {
            id: fee.equipmentId ?? voId,
            type: fee.equipmentId ? QuoteCalcAttachType.Equipment : QuoteCalcAttachType.Invoice,
          },
        },
      };
    });

    return [...equipmentRows, ...feeRows];
  }

  private toChargePriceInput(price?: Nullable<ChargePrice>) {
    return {
      total: price?.total ?? 0,
      markup: price?.markup ?? 0,
      netPrice: price?.netPrice ?? 0,
      discount: price?.discount ?? 0,
      salesTax: price?.salesTax ?? 0,
      markupType: price?.markupType ?? DiscountType.Flat,
      discountType: price?.discountType ?? DiscountType.Flat,
      salesTaxType: price?.salesTaxType ?? DiscountType.Flat,
      retailPrice: price?.retailPrice ?? 0,
      wholesalePrice: price?.wholesalePrice ?? 0,
    };
  }

  // Returns new rows with server-computed prices merged in; never mutates the (form-owned) input rows
  public applyCalcToRows(
    rows: QuoteChargeListingEntity[] = [],
    calc: Nullable<QuoteCalcViewModel>
  ): QuoteChargeListingEntity[] {
    if (!calc?.rows?.length) return rows;

    const byKey = new Map<string, QuoteCalcRowResult>();
    calc.rows.forEach(calcRow => {
      if (calcRow.key) byKey.set(calcRow.key, calcRow);
    });

    return rows.map(row => {
      const key = getRowKey(row);
      const calcRow = key ? byKey.get(key) : undefined;

      if (!calcRow) return row;

      if (row.type === QuoteLineItemType.Equipment && row.equipment && calcRow.equipment) {
        const calcAttachments = calcRow.equipment.attachments ?? [];

        return {
          ...row,
          equipment: {
            ...row.equipment,
            price: this.mergeCalcPrice(row.equipment.price, calcRow.equipment.price),
            attachments: (row.equipment.attachments ?? []).map((attachment, attachmentIndex) => {
              const calcAttachment = calcAttachments[attachmentIndex];

              return calcAttachment
                ? { ...attachment, price: this.mergeCalcPrice(attachment.price, calcAttachment.price) }
                : attachment;
            }),
          },
        };
      }

      if (row.type === QuoteLineItemType.Invoice && row.charge && calcRow.charge) {
        return {
          ...row,
          charge: { ...row.charge, price: this.mergeCalcPrice(row.charge.price, calcRow.charge.price) },
        };
      }

      return row;
    });
  }

  private mergeCalcPrice(price: BasePrice, calcPrice: QuoteCalcRowPrice): BasePrice {
    return {
      ...price,
      ...(calcPrice.total != null ? { total: calcPrice.total } : {}),
      ...(calcPrice.netPrice != null ? { netPrice: calcPrice.netPrice } : {}),
      ...(calcPrice.retailPrice != null ? { retailPrice: calcPrice.retailPrice } : {}),
    };
  }

  private toCalcPrice = (price: BasePrice) => ({
    total: price.total ?? 0,
    markup: price.markup ?? 0,
    netPrice: price.netPrice ?? 0,
    discount: price.discount ?? 0,
    salesTax: price.salesTax ?? 0,
    markupType: price.markupType,
    discountType: price.discountType,
    salesTaxType: price.salesTaxType,
    retailPrice: price.retailPrice ?? 0,
    wholesalePrice: price.wholesalePrice ?? 0,
  });
}

export const quoteCalcMapper = new QuoteCalcMapper();
